import { useState, useEffect } from 'react'
import Img from 'src/components/shared/Img'
import { cn } from 'src/utils/cn'

import Bubble from './Bubble'

const bubblePlacementDesktop = {
  bubble1: { top: 229, left: 1, zIndex: 10 },
  bubble2: { top: 286, left: 74, zIndex: 10 },
  bubble3: { top: 338, left: 28, zIndex: 10 },
  place: { right: 37, bottom: 68, zIndex: 10 },
}

const bubblePlacementMobile = {
  bubble1: { bottom: 140, left: 11, zIndex: 10 },
  bubble2: { bottom: 103, left: 31, zIndex: 10 },
  bubble3: { bottom: 67, left: 11, zIndex: 10 },
  place: { right: 27, bottom: 23, zIndex: 10 },
}

const HeroCarousel = ({ items = [], interval = 10000, dots = true }) => {
  const [activeIndex, setActiveIndex] = useState(0)
  // Slides after the first stay UNMOUNTED until the LCP window has passed.
  // A `loading="lazy"` attribute can't defer them: they sit stacked inside
  // the viewport (absolute, opacity 0), so the browser fetches them right
  // away and they compete with the LCP image for bandwidth.
  const [slidesMounted, setSlidesMounted] = useState(items.length <= 1)
  const [isAutoplayReady, setIsAutoplayReady] = useState(false)
  const [bubbleContent, setBubbleContent] = useState(
    items[activeIndex]?.content
  )
  const [bubblePlacement, setBubblePlacement] = useState(bubblePlacementDesktop)

  const onSetSlider = (index) => {
    // A user tapping a dot overrides the deferral
    setSlidesMounted(true)
    setActiveIndex(index)
  }

  // Mount the remaining slides once the page has loaded and the main thread
  // is idle — by then the hero (LCP) image has painted, so their downloads
  // no longer compete with it.
  useEffect(() => {
    if (items.length <= 1 || slidesMounted) {
      return undefined
    }

    let cancelled = false
    let idleId

    const mountSlides = () => {
      if (!cancelled) setSlidesMounted(true)
    }

    const scheduleMount = () => {
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(mountSlides)
      } else {
        // Safari: yield one macrotask past the load handlers
        idleId = window.setTimeout(mountSlides, 0)
      }
    }

    if (document.readyState === 'complete') {
      scheduleMount()
    } else {
      window.addEventListener('load', scheduleMount, { once: true })
    }

    return () => {
      cancelled = true
      window.removeEventListener('load', scheduleMount)
      if (idleId !== undefined) {
        if (typeof window.cancelIdleCallback === 'function') {
          window.cancelIdleCallback(idleId)
        } else {
          window.clearTimeout(idleId)
        }
      }
    }
  }, [items.length, slidesMounted])

  // Start autoplay once all slides are mounted.
  useEffect(() => {
    if (slidesMounted && items.length > 1) {
      setIsAutoplayReady(true)
    }
  }, [slidesMounted, items.length])

  useEffect(() => {
    if (!isAutoplayReady || items.length <= 1) {
      return
    }

    const maxIndex = items.length - 1
    const autoPlayInterval = setInterval(() => {
      setActiveIndex((prev) => (prev < maxIndex ? prev + 1 : 0))
    }, interval)

    return () => clearInterval(autoPlayInterval)
  }, [interval, items.length, isAutoplayReady])

  useEffect(() => {
    setBubbleContent(items[activeIndex]?.content)
  }, [activeIndex, items, setBubbleContent])

  useEffect(() => {
    const setPlacementByViewport = () => {
      setBubblePlacement(
        window.innerWidth < 1024
          ? bubblePlacementMobile
          : bubblePlacementDesktop
      )
    }

    setPlacementByViewport()

    const onResize = () => {
      setPlacementByViewport()
    }

    window.addEventListener('resize', onResize)

    return () => window.removeEventListener('resize', onResize)
  }, [])

  return (
    <>
      <div className="relative w-full" data-testid="hero-carousel">
        <Bubble
          text={`Resolved ${bubbleContent.resolvedDebtAmount} in debt`}
          variant="small"
          color="green"
          position="absolute"
          placement={bubblePlacement.bubble1}
        />
        <Bubble
          text={`Paid off ${bubbleContent.paidOffDebtsCount} enrolled debts!`}
          variant="small"
          color="orange"
          position="absolute"
          placement={bubblePlacement.bubble2}
        />
        <Bubble
          text={`${bubbleContent.monthlyPaymentCount} monthly payment of ${bubbleContent.monthlyPaymentAmount}`}
          variant="small"
          color="darkBlue"
          position="absolute"
          placement={bubblePlacement.bubble3}
        />
        <Bubble
          className="rounded-tl-none lg:rounded-tl-none"
          text={`${bubbleContent.name}, ${bubbleContent.place}`}
          variant="large"
          color="blue"
          position="absolute"
          placement={bubblePlacement.place}
        />
        <div className="relative w-full" data-testid="hero-carousel-list">
          {items.map((item, index) =>
            index > 0 && !slidesMounted ? null : (
              <div
                className="w-full"
                key={item.src}
                data-testid="hero-carousel-item"
                style={{
                  position: index === activeIndex ? 'relative' : 'absolute',
                  top: 0,
                  left: 0,
                  opacity: index === activeIndex ? 1 : 0,
                  zIndex: index === activeIndex ? 1 : -1,
                  transition: 'opacity .8s linear, z-index .3s linear',
                }}
              >
                <Img
                  src={item.src}
                  alt={item.alt}
                  layout="intrinsic"
                  width={528}
                  height={509}
                  className="w-full"
                  imgStyle={{ width: '100%', height: 'auto' }}
                  priority={index === 0}
                />
              </div>
            )
          )}
        </div>
      </div>
      {dots && (
        <ul
          className="mt-[34px] flex justify-center gap-x-3 lg:hidden"
          data-testid="hero-carousel-dot-list"
        >
          {Array.from(Array(items.length).keys()).map((val) => (
            <li
              key={val}
              data-testid="hero-carousel-dot-item"
              className={cn(
                'size-[4px] rounded-full',
                val === activeIndex ? 'bg-[#2c47f6]' : 'bg-[#c0cbd8]'
              )}
              style={{ transition: 'background-color .5s linear' }}
              onClick={() => onSetSlider(val)}
            />
          ))}
        </ul>
      )}
    </>
  )
}

export default HeroCarousel
